/**
 * brain_memories CRUD + retrieval.
 *
 * Search uses ILIKE on the `fact` column (pg_trgm backs the GIN index) plus
 * tag-array overlap. Ranking is done client-side: exact tag matches first,
 * then confidence, then recency.
 *
 * createMemory dedupes on fuzzy fact match + same scope/entity: if a row
 * exists whose fact shares >60% tokens, bump its confidence instead of
 * inserting. Contradictions ("actually 10-min") are handled by the
 * summarizer — it explicitly supersedes by writing a new row and halving
 * the old row's confidence.
 */
import { supabase } from '@4cm/core/supabase';
import { debug, formatError } from '@4cm/shared';
const log = debug('brain:memory');

export type MemoryScope = 'coach' | 'team' | 'player' | 'session' | 'drill';
export type MemoryKind =
  | 'preference' | 'philosophy' | 'trait' | 'pattern' | 'correction' | 'reflection';

export interface BrainMemory {
  id: string;
  coach_id: string;
  team_id: string | null;
  scope: MemoryScope;
  entity_id: string | null;
  kind: MemoryKind;
  fact: string;
  tags: string[];
  evidence: { source: string; ref_id?: string; excerpt?: string } | null;
  confidence: number;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface CreateMemoryInput {
  coach_id: string;
  team_id?: string | null;
  scope: MemoryScope;
  entity_id?: string | null;
  kind: MemoryKind;
  fact: string;
  tags?: string[];
  evidence?: BrainMemory['evidence'];
  confidence?: number;
}

export interface SearchMemoriesArgs {
  coach_id: string;
  team_id?: string | null;
  scope?: MemoryScope;
  entity_id?: string | null;
  query?: string;          // fuzzy text
  tags?: string[];         // overlap match
  limit?: number;
}

// ---------------------------------------------------------------------------
// Search
// ---------------------------------------------------------------------------

export async function searchMemories(args: SearchMemoriesArgs): Promise<BrainMemory[]> {
  const limit = args.limit ?? 10;
  let q = supabase.from('brain_memories')
    .select('*')
    .eq('coach_id', args.coach_id)
    .is('deleted_at', null)
    .limit(limit * 3);                    // over-fetch, rank client-side

  if (args.scope) q = q.eq('scope', args.scope);
  if (args.entity_id) q = q.eq('entity_id', args.entity_id);
  if (args.team_id) q = q.or(`team_id.eq.${args.team_id},team_id.is.null`);
  if (args.query && args.query.trim().length >= 2) {
    q = q.ilike('fact', `%${args.query.trim()}%`);
  }
  if (args.tags && args.tags.length) {
    q = q.overlaps('tags', args.tags);
  }

  const { data, error } = await q;
  if (error) {
    log.warn('search failed', { message: formatError(error), scope: args.scope, tags: args.tags });
    return [];
  }

  const rows = (data ?? []) as BrainMemory[];
  const tagSet = new Set((args.tags ?? []).map(t => t.toLowerCase()));

  // Rank: tag overlap count → confidence → recency
  rows.sort((a, b) => {
    const aTagHits = a.tags.filter(t => tagSet.has(t.toLowerCase())).length;
    const bTagHits = b.tags.filter(t => tagSet.has(t.toLowerCase())).length;
    if (aTagHits !== bTagHits) return bTagHits - aTagHits;
    if (a.confidence !== b.confidence) return b.confidence - a.confidence;
    return new Date(b.updated_at).getTime() - new Date(a.updated_at).getTime();
  });

  return rows.slice(0, limit);
}

// ---------------------------------------------------------------------------
// Create (with dedupe)
// ---------------------------------------------------------------------------

/** Jaccard token-overlap similarity, 0..1. Rough but fast, no extension needed. */
function similarity(a: string, b: string): number {
  const toks = (s: string) => new Set(
    s.toLowerCase().replace(/[^\w\s]/g, ' ').split(/\s+/).filter(t => t.length >= 3)
  );
  const A = toks(a);
  const B = toks(b);
  if (A.size === 0 || B.size === 0) return 0;
  let inter = 0;
  for (const t of A) if (B.has(t)) inter++;
  return inter / new Set([...A, ...B]).size;
}

export async function createMemory(input: CreateMemoryInput): Promise<BrainMemory | null> {
  const confidence = Math.min(1, Math.max(0, input.confidence ?? 0.6));

  // Dedupe: look for same-scope same-entity rows and check similarity.
  // PostgREST needs `is.null` (not `eq.null`) when entity_id is unset.
  // Only filter on entity_id if it's a valid UUID — non-UUID strings
  // the Brain sometimes supplies (e.g. a player name) would 400 here too.
  const dedupeEntityId = isUuid(input.entity_id) ? input.entity_id : null;
  let q = supabase.from('brain_memories')
    .select('*')
    .eq('coach_id', input.coach_id)
    .eq('scope', input.scope)
    .is('deleted_at', null)
    .limit(40);
  q = dedupeEntityId ? q.eq('entity_id', dedupeEntityId) : q.is('entity_id', null);
  const { data: existing } = await q;

  const match = (existing ?? []).find(row => similarity(row.fact, input.fact) > 0.6);
  if (match) {
    // Raise confidence on repetition; cap at 1.0.
    const newConf = Math.min(1, (match.confidence ?? 0.6) + 0.1);
    const { data } = await supabase.from('brain_memories')
      .update({ confidence: newConf, updated_at: new Date().toISOString() })
      .eq('id', match.id)
      .select('*').single();
    return (data as BrainMemory | null) ?? null;
  }

  // Sanitize inputs — the Brain occasionally hands us a player name or
  // other non-UUID string where entity_id should be a UUID, which the
  // Postgres `uuid` column rejects with a flat 400. Nullify anything that
  // doesn't look like a UUID so the insert succeeds (the fact itself
  // usually names the entity in prose).
  const entity_id = isUuid(input.entity_id) ? input.entity_id : null;
  const team_id = isUuid(input.team_id) ? input.team_id : null;

  const { data, error } = await supabase.from('brain_memories').insert({
    coach_id: input.coach_id,
    team_id,
    scope: input.scope,
    entity_id,
    kind: input.kind,
    fact: input.fact,
    tags: input.tags ?? [],
    evidence: input.evidence ?? null,
    confidence,
  }).select('*').single();
  if (error) {
    log.warn('create failed', {
      message: formatError(error),
      scope: input.scope,
      kind: input.kind,
      team_id,
      entity_id,
      fact_len: input.fact?.length ?? 0,
    });
    return null;
  }
  return data as BrainMemory;
}

function isUuid(v: unknown): v is string {
  return typeof v === 'string'
    && /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(v);
}

// ---------------------------------------------------------------------------
// Update / Delete
// ---------------------------------------------------------------------------

export async function updateMemory(id: string, patch: Partial<Pick<BrainMemory, 'fact' | 'tags' | 'confidence' | 'kind'>>): Promise<BrainMemory | null> {
  const { data, error } = await supabase.from('brain_memories')
    .update({ ...patch, updated_at: new Date().toISOString() })
    .eq('id', id)
    .select('*').single();
  if (error) { log.warn('update failed', { message: formatError(error), id }); return null; }
  return data as BrainMemory;
}

export async function softDeleteMemory(id: string): Promise<boolean> {
  const { error } = await supabase.from('brain_memories')
    .update({ deleted_at: new Date().toISOString() })
    .eq('id', id);
  if (error) { log.warn('delete failed', { message: formatError(error), id }); return false; }
  return true;
}

/** Halve confidence on a memory that's been contradicted. */
export async function weakenMemory(id: string): Promise<void> {
  const { data } = await supabase.from('brain_memories').select('confidence').eq('id', id).single();
  const next = Math.max(0.1, (data?.confidence ?? 0.5) * 0.5);
  await supabase.from('brain_memories')
    .update({ confidence: next, updated_at: new Date().toISOString() })
    .eq('id', id);
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/** Derive tags from a screen context so retrieval can weight recent context. */
export function screenContextToTags(ctx: {
  kind: string; teamId?: string | null; sessionId?: string | null; playerId?: string | null;
}): string[] {
  const tags: string[] = [`screen:${ctx.kind}`];
  if (ctx.teamId) tags.push(`team:${ctx.teamId}`);
  if (ctx.sessionId) tags.push(`session:${ctx.sessionId}`);
  if (ctx.playerId) tags.push(`player:${ctx.playerId}`);
  return tags;
}
